import React, { useState, useEffect, useCallback } from 'react';
import { Box, Typography, Alert, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, ToggleButton, ToggleButtonGroup, Pagination, FormControl, InputLabel, Select, MenuItem } from '@mui/material';
import { useAppContext } from '../context/AppContext';
import BrowserCanopyList from '../components/BrowserCanopyList';
import ScopedTelemetryBrowser from '../components/ScopedTelemetryBrowser';
import { useAuth } from '../auth/AuthContext';
import Datalens from '../components/Datalens';

/**
 * BrowserView: Browse telemetry records for selected canopies in a fleet.
 * Layout matches FleetView: right-side canopy list, main panel for records.
 * Single canopy selection only - no multi-canopy comparison.
 * Main panel is a table (Browser Panel) with sticky header, scrollable body, and selectable rows.
 * Clicking a row opens Datalens as a modal popup.
 * Supports both live telemetry data and historical telemetry_history data.
 * Pagination support for handling thousands of records efficiently.
 */
const LegacyBrowserView = () => {
  const { customerContext, fleetContext } = useAppContext();
  const [selectedCanopy, setSelectedCanopy] = useState(null);
  const [telemetryRecords, setTelemetryRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedRow, setSelectedRow] = useState(null);
  const [datalensOpen, setDatalensOpen] = useState(false);
  const [dataSource, setDataSource] = useState('live'); // 'live' or 'historical'

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [recordsPerPage, setRecordsPerPage] = useState(100);
  const [totalRecords, setTotalRecords] = useState(0);

  // Fetch telemetry records for selected canopy with pagination
  const fetchTelemetryRecords = useCallback(async (canopyId, page = 1, limit = 100) => {
    if (!canopyId || !customerContext?.customerId || !fleetContext?.fleetId) {
      setTelemetryRecords([]);
      setTotalRecords(0);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const offset = (page - 1) * limit;
      const endpoint = dataSource === 'live'
        ? `https://sabalsql-dbam-ji2csljmda-uk.a.run.app/api/telemetry/data/live/${encodeURIComponent(customerContext.customerId)}/${fleetContext.fleetId}/${canopyId}?limit=${limit}&offset=${offset}`
        : `https://sabalsql-dbam-ji2csljmda-uk.a.run.app/api/telemetry/data/historical/${encodeURIComponent(customerContext.customerId)}/${fleetContext.fleetId}/${canopyId}?limit=${limit}&offset=${offset}`;

      const response = await fetch(endpoint);
      const data = await response.json();
      setTelemetryRecords(data.records || []);
      setTotalRecords(data.metadata?.totalRecords || 0);
    } catch (err) {
      setError(`Failed to fetch ${dataSource} telemetry records`);
      setTelemetryRecords([]);
      setTotalRecords(0);
    } finally {
      setLoading(false);
    }
  }, [customerContext?.customerId, fleetContext?.fleetId, dataSource]);

  // Refetch data when key inputs change (data source, selected canopy, pagination)
  useEffect(() => {
    if (selectedCanopy) {
      fetchTelemetryRecords(selectedCanopy, currentPage, recordsPerPage);
    }
  }, [selectedCanopy, currentPage, recordsPerPage, dataSource, fetchTelemetryRecords]);

  // Handle canopy selection from BrowserCanopyList (single selection)
  const handleCanopySelection = (canopyIds) => {
    const canopyId = canopyIds.length > 0 ? canopyIds[0] : null;
    setSelectedCanopy(canopyId);
    setCurrentPage(1); // Reset to first page when selecting new canopy
    fetchTelemetryRecords(canopyId, 1, recordsPerPage);
    setSelectedRow(null); // Clear row selection on new canopy
    setDatalensOpen(false);
  };

  // Handle data source change
  const handleDataSourceChange = (event, newDataSource) => {
    if (newDataSource !== null) {
      setDataSource(newDataSource);
      setCurrentPage(1); // Reset to first page when changing data source
      setTelemetryRecords([]); // Immediately clear existing data
      setTotalRecords(0); // Reset total records
      setError(null); // Clear any previous errors
      if (selectedCanopy) {
        fetchTelemetryRecords(selectedCanopy, 1, recordsPerPage);
      }
    }
  };

  // Handle page change
  const handlePageChange = (event, newPage) => {
    setCurrentPage(newPage);
    if (selectedCanopy) {
      fetchTelemetryRecords(selectedCanopy, newPage, recordsPerPage);
    }
  };

  // Handle records per page change
  const handleRecordsPerPageChange = (event) => {
    const newLimit = event.target.value;
    setRecordsPerPage(newLimit);
    setCurrentPage(1); // Reset to first page when changing page size
    if (selectedCanopy) {
      fetchTelemetryRecords(selectedCanopy, 1, newLimit);
    }
  };

  // Handle row selection and Datalens open
  const handleRowClick = (rowIdx) => {
    setSelectedRow(rowIdx);
    setDatalensOpen(true);
  };

  // Handle Datalens close
  const handleDatalensClose = () => {
    setDatalensOpen(false);
    setSelectedRow(null);
  };

  // Calculate pagination info
  const totalPages = Math.ceil(totalRecords / recordsPerPage);
  const startRecord = totalRecords > 0 ? (currentPage - 1) * recordsPerPage + 1 : 0;
  const endRecord = Math.min(currentPage * recordsPerPage, totalRecords);

  if (!customerContext || !fleetContext) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="info">
          Please select a customer and fleet to browse telemetry data.
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider', bgcolor: 'background.paper' }}>
        <Typography variant="h5" component="h1" gutterBottom>
          Telemetry Browser
        </Typography>
        <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
          Browse telemetry records for selected canopies in {fleetContext.fleetName || fleetContext.fleetId}
        </Typography>

        {/* Data Source Toggle */}
        <ToggleButtonGroup
          value={dataSource}
          exclusive
          onChange={handleDataSourceChange}
          aria-label="data source"
          size="small"
        >
          <ToggleButton value="live" aria-label="current data">
            Current Data
          </ToggleButton>
          <ToggleButton value="historical" aria-label="archived load">
            Archived Load
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {/* Main Content */}
      <Box sx={{ flexGrow: 1, display: 'flex', overflow: 'hidden' }}>
        {/* Browser Panel (Left side) */}
        <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', p: 2, minWidth: 0 }}>
          {selectedCanopy ? (
            <>
              <Typography variant="h6" gutterBottom>
                {dataSource === 'live' ? 'Live' : 'Historical'} Telemetry Records for Canopy {selectedCanopy}
              </Typography>

              {/* Pagination Controls */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Showing {startRecord}-{endRecord} of {totalRecords} records
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <FormControl size="small" sx={{ minWidth: 120 }}>
                    <InputLabel>Records per page</InputLabel>
                    <Select
                      value={recordsPerPage}
                      label="Records per page"
                      onChange={handleRecordsPerPageChange}
                    >
                      <MenuItem value={50}>50</MenuItem>
                      <MenuItem value={100}>100</MenuItem>
                      <MenuItem value={200}>200</MenuItem>
                      <MenuItem value={500}>500</MenuItem>
                    </Select>
                  </FormControl>
                  <Pagination
                    count={totalPages}
                    page={currentPage}
                    onChange={handlePageChange}
                    size="small"
                    showFirstButton
                    showLastButton
                  />
                </Box>
              </Box>

              {loading ? (
                <Typography>Loading {dataSource} telemetry records...</Typography>
              ) : telemetryRecords.length > 0 ? (
                <Paper sx={{ flexGrow: 1, overflow: 'auto', minHeight: 0 }}>
                  <TableContainer sx={{ maxHeight: '60vh' }}>
                    <Table stickyHeader size="small" aria-label="telemetry records table">
                      <TableHead>
                        <TableRow>
                          <TableCell>Timestamp</TableCell>
                          <TableCell>Device ID</TableCell>
                          <TableCell>Sequence</TableCell>
                          <TableCell>State</TableCell>
                          <TableCell>Error Codes</TableCell>
                          <TableCell>Tilt Angle</TableCell>
                          {dataSource === 'historical' && (
                            <TableCell>Import Source</TableCell>
                          )}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {telemetryRecords.map((record, idx) => (
                          <TableRow
                            key={record.id || idx}
                            hover
                            selected={selectedRow === idx}
                            onClick={() => handleRowClick(idx)}
                            sx={{ cursor: 'pointer' }}
                          >
                            <TableCell>{new Date(record.timestamp).toLocaleString()}</TableCell>
                            <TableCell>{record.device_id}</TableCell>
                            <TableCell>{record.sequence_number}</TableCell>
                            <TableCell>{record.canopy_state || 'N/A'}</TableCell>
                            <TableCell>{record.error_codes && record.error_codes.length > 0 ? record.error_codes.join(', ') : ''}</TableCell>
                            <TableCell>{record.tilt_angle !== undefined ? `${record.tilt_angle}°` : ''}</TableCell>
                            {dataSource === 'historical' && (
                              <TableCell>
                                {record.import_source ? (
                                  <Typography variant="caption" color="text.secondary">
                                    {record.import_source}
                                  </Typography>
                                ) : 'N/A'}
                              </TableCell>
                            )}
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Paper>
              ) : (
                <Typography color="textSecondary">
                  No {dataSource} telemetry records found for this canopy.
                </Typography>
              )}

              {/* Bottom Pagination */}
              {totalRecords > 0 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                  <Pagination
                    count={totalPages}
                    page={currentPage}
                    onChange={handlePageChange}
                    showFirstButton
                    showLastButton
                  />
                </Box>
              )}

              {/* Datalens Modal Popup */}
              <Datalens
                record={selectedRow !== null ? telemetryRecords[selectedRow] : null}
                open={datalensOpen}
                onClose={handleDatalensClose}
                modal={true}
                dataSource={dataSource}
              />
            </>
          ) : (
            <Typography color="textSecondary">
              Select a canopy from the list to view {dataSource} telemetry records.
            </Typography>
          )}
          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>
          )}
        </Box>

        {/* Canopy List (Right side) */}
        <Box sx={{ width: 300, borderLeft: 1, borderColor: 'divider', bgcolor: 'background.paper' }}>
          <BrowserCanopyList onCanopySelection={handleCanopySelection} />
        </Box>
      </Box>
    </Box>
  );
};

export default function BrowserView() {
  const auth = useAuth();
  return auth.isGuest ? <ScopedTelemetryBrowser /> : <LegacyBrowserView />;
}
